import { useContext, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import isEqual from 'lodash-es/isEqual';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@maplibre/maplibre-gl-leaflet';
import AppContext, { OBJECT_TYPE_POI, updateConfigureMapCache } from '../../context/AppContext';
import MapContext from '../../context/MapContext';
import MvtContext from '../../context/MvtContext';
import { osmandTileURL } from '../baseTileURL';
import { isOsmAndTileURL, isWebGLAvailable } from './MvtLayerConfig';
import { MENU_INFO_OPEN_SIZE, POI_LAYER_ID } from '../../manager/GlobalManager';
import { isTravelSearchOn } from './TravelLayer';
import { createMvtObject, pickClickableFeatures } from '../util/MvtObjectSelection';
import {
    getMvtDataTileUrl,
    getMvtTileStats,
    getMvtTileZoom,
    setMapDataZoomShift,
    setMapStyleDetailShift,
    watchMvtZoom,
} from '../util/MvtMapUtils';
import {
    ensureLeafletPane,
    setMapHybridVisibility,
    setStyleHybridVisibility,
    useHybridUnderlayUrl,
} from './MvtHybridDemo';

const SHOW_TILE_BOUNDARIES = true;
const TILE_SOURCES_KEY = '__osmandMvtTileSources';
const POINTER_CURSOR = 'pointer';
// as in Android MapSelectionHelper (20 px on touch screens)
const CLICK_TOLERANCE_PX = 5;

function getPublicAssetPath(path) {
    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    return `${publicUrl}${path}`;
}

function getPublicAssetUrl(path) {
    return new URL(getPublicAssetPath(path), window.location.origin).toString();
}

function createStyle(baseStyle, tileUrl, options = {}) {
    const style = structuredClone(baseStyle);
    style.sources = {
        ...style.sources,
        osm: {
            ...style.sources.osm,
            tiles: [tileUrl],
        },
    };
    style.sprite = getPublicAssetUrl('/mvt/sprites/sprite');
    style.glyphs = getPublicAssetPath('/mvt/fonts/{fontstack}/{range}.pbf');
    if (options.hideHybridLayers) {
        setStyleHybridVisibility(style, 'none');
    }
    return style;
}

function getMvtSources(config) {
    const sources = createStyle(config.style, config.tileUrl).sources || {};
    return Object.entries(sources).flatMap(([id, source]) =>
        source?.type === 'vector' && source.tiles?.length
            ? [
                  {
                      id,
                      url: source.tiles[0],
                      minzoom: source.minzoom ?? 0,
                      maxzoom: source.maxzoom ?? 22,
                      scheme: source.scheme,
                  },
              ]
            : []
    );
}

function getTileCoord({ lat, lng }, source, zoom) {
    const z = getMvtTileZoom(source, zoom);
    const n = 2 ** z;
    const sin = Math.sin((Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI) / 180);
    const x = Math.floor((((L.Util.wrapNum(lng, [-180, 180], true) + 180) / 360) * n) % n);
    const y = Math.max(0, Math.min(n - 1, Math.floor((0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * n)));

    return { z, x, y: source.scheme === 'tms' ? n - y - 1 : y };
}

export function getMvtTileDownloads(map, latlng) {
    if (!map || !latlng) {
        return [];
    }

    return (map?.[TILE_SOURCES_KEY] || []).map((source) => {
        const { z, x, y } = getTileCoord(latlng, source, source.getZoom?.() ?? map.getZoom());
        return {
            url: source.url.replace('{z}', z).replace('{x}', x).replace('{y}', y),
            name: `${source.layerKey || source.id}-${z}-${x}-${y}.mvt`,
        };
    });
}

function resetPointerCursor(container) {
    if (container.style.cursor === POINTER_CURSOR) {
        container.style.cursor = '';
    }
}

export default function MvtLayer({ config }) {
    const map = useMap();
    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);
    const vtx = useContext(MvtContext);
    const hybridUnderlayUrl = useHybridUnderlayUrl();
    const hybridUnderlayUrlRef = useRef(hybridUnderlayUrl);
    const maplibreMapRef = useRef(null);
    const dataZoomShift = ctx.develFeatures && isOsmAndTileURL(mtx.tileURL) ? vtx.mvtTweaks.dataZoomShift : 0;

    hybridUnderlayUrlRef.current = hybridUnderlayUrl;

    useEffect(() => {
        const { style, tileUrl, isActive, clickable, errorLabel, pane: paneName, paneZIndex } = config;

        if (!isActive(mtx.tileURL)) {
            return undefined;
        }

        window.seIsTilesLoaded = false;

        if (!isWebGLAvailable()) {
            window.seIsTilesLoaded = true;
            console.warn(`${errorLabel}: WebGL is not available`);
            mtx.setTileURL(osmandTileURL);
            const configureMap = {
                ...ctx.configureMapState,
                mapStyle: { tileURL: osmandTileURL },
            };
            updateConfigureMapCache(configureMap);
            ctx.setConfigureMapState(configureMap);
            return undefined;
        }

        if (paneName) {
            ensureLeafletPane(map, paneName, paneZIndex);
        }

        const shiftedTileUrl = getMvtDataTileUrl(tileUrl, dataZoomShift);
        const glLayer = L.maplibreGL({
            style: createStyle(style, shiftedTileUrl, {
                hideHybridLayers: Boolean(hybridUnderlayUrlRef.current),
            }),
            interactive: false,
            ...(paneName ? { pane: paneName } : {}),
        }).addTo(map);

        const maplibreMap = glLayer.getMaplibreMap();
        maplibreMapRef.current = maplibreMap;
        maplibreMap.showTileBoundaries = SHOW_TILE_BOUNDARIES && ctx.develFeatures === true;

        const sourceOwner = Symbol(config.tileUrl);
        const sources = getMvtSources({ ...config, tileUrl: shiftedTileUrl }).map((source) => ({
            ...source,
            sourceOwner,
            layerKey: mtx.tileURL?.key,
            getZoom: () => maplibreMap.getZoom(),
        }));
        map[TILE_SOURCES_KEY] = [...(map[TILE_SOURCES_KEY] || []), ...sources];
        const stopZoomStats = ctx.develFeatures ? watchMvtZoom(map, sources, vtx.setMvtTileStats) : null;
        const tileStatsCache = ctx.develFeatures ? new WeakMap() : null;

        const handleLoading = () => {
            window.seIsTilesLoaded = false;
            if (ctx.develFeatures) {
                vtx.setMvtTileStats((stats) =>
                    stats?.count == null && stats?.bytes == null
                        ? stats
                        : { ...stats, bytes: null, features: null, vertices: null, count: null }
                );
            }
        };

        const handleIdle = () => {
            // the first idle comes before the tiles of the view are loaded
            if (maplibreMap.loaded() && maplibreMap.areTilesLoaded()) {
                window.seIsTilesLoaded = true;
                if (ctx.develFeatures) {
                    const nextStats = getMvtTileStats(maplibreMap, map, tileStatsCache);
                    vtx.setMvtTileStats((stats) => (isEqual(stats, nextStats) ? stats : nextStats));
                }
            }
        };

        const handleError = (event) => {
            console.warn(errorLabel, event?.error ?? event);
        };

        const getClickableFeatures = (mouseEvent) => {
            const canvas = glLayer.getCanvas();
            const rect = canvas.getBoundingClientRect();
            const x = mouseEvent.clientX - rect.left;
            const y = mouseEvent.clientY - rect.top;
            const box = [
                [x - CLICK_TOLERANCE_PX, y - CLICK_TOLERANCE_PX],
                [x + CLICK_TOLERANCE_PX, y + CLICK_TOLERANCE_PX],
            ];
            try {
                return pickClickableFeatures(maplibreMap.queryRenderedFeatures(box));
            } catch (error) {
                return [];
            }
        };

        // DOM listener runs after Leaflet map handlers (NavigationLayer resets the cursor on map mousemove)
        const handleMouseMove = (mouseEvent) => {
            const container = map.getContainer();
            if (!isTravelSearchOn(map) && getClickableFeatures(mouseEvent).length > 0) {
                container.style.cursor = POINTER_CURSOR;
            } else {
                resetPointerCursor(container);
            }
        };

        const handleMapClick = (event) => {
            if (isTravelSearchOn(map)) {
                return;
            }
            const features = getClickableFeatures(event.originalEvent);
            if (features.length === 0) {
                return;
            }
            // all objects under the click, the menu shows them one by one
            const objects = features.map((feature) => createMvtObject(feature, event.latlng));
            const obj = { ...objects[0], mvtPreviews: objects };
            ctx.setCurrentObjectType(OBJECT_TYPE_POI);
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
            // as after a marker hover: keeps GlobalFrame from opening the POI by its url
            ctx.setSelectedWptId({ id: obj.mvt.osmId, show: false, type: POI_LAYER_ID });
            // no selectedWpt.id: SearchLayer (useSelectMarkerOnMap) would draw a selected pin for it
            ctx.setSelectedWpt(obj);
        };

        if (clickable) {
            map.on('click', handleMapClick);
            L.DomEvent.on(map.getContainer(), 'mousemove', handleMouseMove);
        }
        maplibreMap.on('dataloading', handleLoading);
        maplibreMap.on('idle', handleIdle);
        maplibreMap.on('error', handleError);

        return () => {
            window.seIsTilesLoaded = true;
            if (clickable) {
                map.off('click', handleMapClick);
                L.DomEvent.off(map.getContainer(), 'mousemove', handleMouseMove);
                resetPointerCursor(map.getContainer());
            }
            maplibreMap.off('dataloading', handleLoading);
            maplibreMap.off('idle', handleIdle);
            maplibreMap.off('error', handleError);
            stopZoomStats?.();
            if (ctx.develFeatures) {
                vtx.setMvtTileStats(null);
                vtx.setMvtStyleUpdating(false);
            }
            map[TILE_SOURCES_KEY] = (map[TILE_SOURCES_KEY] || []).filter(
                (source) => source.sourceOwner !== sourceOwner
            );
            maplibreMapRef.current = null;
            map.removeLayer(glLayer);
        };
    }, [map, mtx.tileURL, config, ctx.develFeatures]);

    useEffect(() => {
        const maplibreMap = maplibreMapRef.current;
        if (!maplibreMap || !config.isActive(mtx.tileURL) || !isOsmAndTileURL(mtx.tileURL)) {
            return undefined;
        }
        const applyShift = () => {
            const sources = (map[TILE_SOURCES_KEY] || []).filter((source) => source.layerKey === mtx.tileURL.key);
            setMapDataZoomShift(maplibreMap, sources, config.tileUrl, dataZoomShift);
        };
        if (maplibreMap.isStyleLoaded()) {
            applyShift();
        } else {
            maplibreMap.once('idle', applyShift);
        }

        return () => maplibreMap.off('idle', applyShift);
    }, [config, mtx.tileURL, ctx.develFeatures, dataZoomShift]);

    useEffect(() => {
        const maplibreMap = maplibreMapRef.current;
        if (!maplibreMap || !config.isActive(mtx.tileURL)) {
            return undefined;
        }

        const finishStyle = () => vtx.setMvtStyleUpdating(false);
        const applyStyle = () => {
            setMapHybridVisibility(maplibreMap, config.style, Boolean(hybridUnderlayUrl));
            setMapStyleDetailShift(
                maplibreMap,
                config.style,
                ctx.develFeatures ? vtx.mvtTweaks.styleDetailShift : 0,
                vtx.mvtTweaks.minZoomIdFilter
            );
            maplibreMap.once('idle', finishStyle);
        };

        if (maplibreMap.isStyleLoaded()) {
            applyStyle();
        } else {
            maplibreMap.once('idle', applyStyle);
        }

        return () => {
            maplibreMap.off('idle', applyStyle);
            maplibreMap.off('idle', finishStyle);
        };
    }, [
        config,
        mtx.tileURL,
        hybridUnderlayUrl,
        ctx.develFeatures,
        vtx.mvtTweaks.styleDetailShift,
        vtx.mvtTweaks.minZoomIdFilter,
    ]);

    return null;
}
